import {
  ConflictReasonDto,
  DefinitionEventKindDto,
  PlannedChangeKindDto,
  SurveyGapCodeDto,
  VersionStatusDto,
} from '@/api/generated';
import { StatusActionEnum } from '@/models/status/StatusAction.enum.ts';

export type ComponentsTranslation = {
  confirmDialog: {
    cancel: string;
  };
  conflict: {
    reasons: Record<ConflictReasonDto, string>;
    unknown: string;
  };
  dataTable: {
    displayedRows: string;
    loadingLabel: string;
    rowsPerPage: string;
    sortAscending: string;
    sortDescending: string;
  };
  definitionHistory: {
    after: string;
    before: string;
    description: string;
    empty: string;
    eventMeta: string;
    events: Record<DefinitionEventKindDto, string>;
    otherField: string;
    title: string;
    values: {
      added: string;
      no: string;
      none: string;
      removed: string;
      yes: string;
    };
  };
  emptyState: {
    noData: {
      description: string;
      title: string;
    };
    noMatch: {
      clearFilters: string;
      description: string;
      title: string;
    };
  };
  errorState: {
    description: string;
    retry: string;
    title: string;
  };
  form: {
    characterCount: string;
    fieldRejected: string;
    forbidden: string;
    requiredMark: string;
    serverRejected: string;
    unexpectedError: string;
  };
  loadingState: {
    label: string;
  };
  mediaPreview: {
    close: string;
    enlarge: string;
    imageAlt: string;
    imageAltUnnamed: string;
    loadFailed: string;
    retry: string;
    viewerLabel: string;
  };
  mutationFeedback: {
    forbidden: string;
  };
  notifications: {
    close: string;
  };
  positionSelect: {
    loading: string;
    noOptions: string;
    outdated: string;
    unavailable: string;
  };
  questionImpact: {
    affected: {
      description: string;
      title: string;
    };
    cancel: string;
    checking: string;
    error: {
      description: string;
      retry: string;
      title: string;
    };
    none: Record<PlannedChangeKindDto, string>;
    noneAfterChanges: string;
    part: string;
    position: string;
    shortfall: string;
    surveyGaps: Record<SurveyGapCodeDto, string>;
    surveys: string;
    tests: string;
    unknownQuestion: string;
  };
  questionPresentation: {
    answerKey: string;
    answerMediaLabel: string;
    answerPlaceholder: string;
    answerPoints: string;
    answersLabel: string;
    correct: string;
    emptyAnswer: string;
    emptyBody: string;
    examiner: {
      answerKey: string;
      comment: string;
      commentPlaceholder: string;
      commentRequired: string;
      fail: string;
      pass: string;
      points: string;
      pointsHint: string;
      result: string;
      scale: string;
      topicNumber: string;
      topics: string;
      topicsPicked: string;
      topicsToPick_few?: string;
      topicsToPick_many?: string;
      topicsToPick_one: string;
      topicsToPick_other?: string;
    };
    expectedAnswers: string;
    hiddenMarker: Record<Exclude<VersionStatusDto, typeof VersionStatusDto.Active>, string>;
    missingTranslation: string;
    noVersion: string;
    openPlaceholder: string;
    ordering: {
      correctPosition: string;
      hint: string;
      moveDown: string;
      moveUp: string;
    };
    questionMediaLabel: string;
    showCorrect: string;
    solution: {
      explanation: string;
      maxPoints: string;
    };
    unapprovedMarker: string;
  };
  reasonDialog: {
    cancel: string;
    reason: string;
    reasonPlaceholder: string;
  };
  reviewMark: {
    clear: string;
    dialog: {
      cancel: string;
      confirm: string;
      description: string;
      reason: string;
      reasonPlaceholder: string;
      title: string;
    };
    mark: string;
    markedBy: string;
    noReason: string;
    notifications: {
      cleared: string;
      clearFailed: string;
      marked: string;
      markFailed: string;
    };
    title: string;
  };
  searchField: {
    clear: string;
    minLength_few?: string;
    minLength_many?: string;
    minLength_one: string;
    minLength_other?: string;
    placeholder: string;
  };
  statusAction: {
    actions: Record<StatusActionEnum, string>;
    saveFirst: string;
  };
  testPerson: {
    cannotStart: string;
    canStart: string;
    fields: {
      granted: string;
      invalidated: string;
      left: string;
      limit: string;
      used: string;
    };
    forbidden: {
      description: string;
      title: string;
    };
    grants: {
      empty: string;
      meta: string;
      title: string;
    };
    lock: {
      description: string;
      title: string;
    };
    lockedAfterPass: string;
    noLimit: string;
    passed: string;
  };
  uploadField: {
    tooLarge: string;
    tooManyFiles: string;
    wrongFormat: string;
  };
};

export const componentsTranslation: ComponentsTranslation = {
  confirmDialog: {
    cancel: 'Cancel',
  },
  conflict: {
    reasons: {
      [ConflictReasonDto.AlreadyInvalidated]: 'This attempt has already been invalidated.',
      [ConflictReasonDto.AlreadyPassed]:
        'This test has already been passed, so another attempt is not possible.',
      [ConflictReasonDto.AnonymityFixed]:
        'The survey already has answers, so its anonymity cannot change.',
      [ConflictReasonDto.AttemptInProgress]:
        'This attempt is still in progress. Its result will be available once it ends.',
      [ConflictReasonDto.AttemptLimitReached]: 'Every attempt at this test has been used.',
      [ConflictReasonDto.AttemptOver]: 'This attempt has already ended or its time ran out.',
      [ConflictReasonDto.Clash]:
        'Someone changed this in the meantime. Refresh the page and try again.',
      [ConflictReasonDto.ConfirmationRequired]:
        'This change covers many questions and needs confirming. Confirm it and try again.',
      [ConflictReasonDto.ExaminerRequired]:
        'This test needs an examiner to mark it, so it cannot start yet.',
      [ConflictReasonDto.GoingBackNotAllowed]:
        'This test does not allow going back to earlier questions.',
      [ConflictReasonDto.InUse]: 'This cannot be deleted, because it is in use.',
      [ConflictReasonDto.KeyTaken]: 'This question key is already taken. Enter a different one.',
      [ConflictReasonDto.MaterialInForce]:
        'An answer with material cannot be removed from the active version. To remove it, create a new version of the question.',
      [ConflictReasonDto.MissingRequired]: 'Answer every required question to send the survey.',
      [ConflictReasonDto.NameTaken]: 'This name is already taken. Enter a different one.',
      [ConflictReasonDto.NeverInUse]:
        'A draft cannot be turned off. If you no longer need it, delete it.',
      [ConflictReasonDto.NotAnExam]: 'An examiner runs only a practical exam.',
      [ConflictReasonDto.NotReady]: 'Some things are still missing. Fill them in and try again.',
      [ConflictReasonDto.PersonLocked]: "This person's attempts at this test are locked.",
      [ConflictReasonDto.PrefixInUse]:
        'The prefix cannot change, because question keys have already been built from it.',
      [ConflictReasonDto.PrefixTaken]:
        'Another category already has this prefix. Enter a different one.',
      [ConflictReasonDto.ResponseOver]: 'This survey has already been sent or refused.',
      [ConflictReasonDto.SurveyNotActive]: 'This survey is not active, so it cannot be started.',
      [ConflictReasonDto.TestNotActive]: 'This test is not active, so it cannot be started.',
      [ConflictReasonDto.Unanswered]: 'Answer every question to finish the test.',
      [ConflictReasonDto.Ungraded]: 'Grade every question to finish the exam.',
      [ConflictReasonDto.VersionStatus]:
        'The status of this question version does not allow this change.',
    },
    unknown: 'Could not save, because the current state of the data does not allow it.',
  },
  dataTable: {
    displayedRows: '{{from}}–{{to}} of {{count}}',
    loadingLabel: 'Loading rows',
    rowsPerPage: 'Rows per page',
    sortAscending: 'sorted ascending',
    sortDescending: 'sorted descending',
  },
  definitionHistory: {
    after: 'After',
    before: 'Before',
    description: 'Changes from the newest, each with its author and time.',
    empty: 'Nothing has changed yet.',
    eventMeta: '{{date}}, {{author}}',
    events: {
      [DefinitionEventKindDto.Changed]: 'Changed',
      [DefinitionEventKindDto.Created]: 'Created',
      [DefinitionEventKindDto.StatusChanged]: 'Status changed',
    },
    otherField: 'Other change',
    title: 'Change history',
    values: {
      added: 'Added',
      no: 'no',
      none: 'none',
      removed: 'Removed',
      yes: 'yes',
    },
  },
  emptyState: {
    noData: {
      description: 'Items you add will appear here.',
      title: 'Nothing here yet',
    },
    noMatch: {
      clearFilters: 'Clear filters',
      description: 'Change the search phrase or clear the filters to see more results.',
      title: 'No matching results',
    },
  },
  errorState: {
    description: 'The data could not be loaded. Check your connection and try again.',
    retry: 'Try again',
    title: 'Something went wrong',
  },
  form: {
    characterCount: '{{length}} / {{max}}',
    fieldRejected: 'Check this field.',
    forbidden: 'You do not have permission to save these changes.',
    requiredMark: 'required',
    serverRejected: 'Some fields need attention. Correct them and save again.',
    unexpectedError: 'Changes could not be saved. Try again.',
  },
  loadingState: {
    label: 'Loading',
  },
  mediaPreview: {
    close: 'Close the enlarged image',
    enlarge: 'Enlarge image',
    imageAlt: 'Media: {{name}}',
    imageAltUnnamed: 'Image for the question',
    loadFailed: 'Could not load the media.',
    retry: 'Load again',
    viewerLabel: 'Enlarged image',
  },
  mutationFeedback: {
    forbidden: 'You do not have permission to do this.',
  },
  notifications: {
    close: 'Close notification',
  },
  positionSelect: {
    loading: 'Loading positions',
    noOptions: 'No matching positions',
    outdated: '{{label}} (outdated)',
    unavailable: 'Could not load positions. Refresh the page.',
  },
  questionImpact: {
    affected: {
      description: 'You can still go ahead. Add the missing questions afterwards.',
      title: 'Active tests or surveys will run short of questions',
    },
    cancel: 'Cancel',
    checking: 'Checking whether the change affects active tests and surveys',
    error: {
      description: 'Try again, or go ahead without this check.',
      retry: 'Try again',
      title: 'Could not check whether the change affects active tests and surveys',
    },
    none: {
      [PlannedChangeKindDto.ActivateVersion]:
        'Active tests and surveys will still have enough questions once this version is active.',
      [PlannedChangeKindDto.Retire]:
        'Active tests and surveys will still have enough questions after retiring.',
      [PlannedChangeKindDto.SetCategory]:
        'Active tests and surveys will still have enough questions after the category change.',
      [PlannedChangeKindDto.SetPositions]:
        'Active tests and surveys will still have enough questions after the position change.',
    },
    noneAfterChanges:
      'Active tests and surveys will still have enough questions after these changes.',
    part: 'Part {{number}}',
    position: 'position {{name}}',
    shortfall: '{{label}}: needs {{needed}}, has {{available}} now, {{after}} after the change.',
    surveyGaps: {
      [SurveyGapCodeDto.NoQuestions]: 'The survey will have no questions.',
      [SurveyGapCodeDto.QuestionNotInUse]: 'Question {{key}} will have no active version.',
      [SurveyGapCodeDto.TranslationNotApproved]: 'Question {{key}} will have no approved wording.',
    },
    surveys: 'Surveys',
    tests: 'Tests',
    unknownQuestion: 'from the list',
  },
  questionPresentation: {
    answerKey: 'Answer key',
    answerMediaLabel: 'Answer media',
    answerPlaceholder: 'Type your answer',
    answerPoints: 'Points: {{points}}',
    answersLabel: 'Answers',
    correct: 'Correct',
    emptyAnswer: 'Empty answer',
    emptyBody: 'The question text will appear here.',
    examiner: {
      answerKey: 'Key for the examiner',
      comment: 'Examiner comment',
      commentPlaceholder: 'Note what shaped the grade. The person examined never sees it.',
      commentRequired: 'Examiner comment (required)',
      fail: 'Failed',
      pass: 'Passed',
      points: 'Points',
      pointsHint: 'From 0 to {{max}}, one decimal place at most.',
      result: 'Result',
      scale: 'Score from 0 to {{max}}',
      topicNumber: '{{number}}.',
      topics: 'Topics',
      topicsPicked: '{{picked}} of {{total}} picked',
      topicsToPick_one: 'The examiner picks {{count}} topic.',
      topicsToPick_other: 'The examiner picks {{count}} topics.',
    },
    expectedAnswers: 'Expected answer',
    hiddenMarker: {
      [VersionStatusDto.Draft]: 'Draft only, employees do not see it',
      [VersionStatusDto.Retired]: 'Retired, employees do not see it',
    },
    missingTranslation:
      'This question has no approved text in this language, so it will not appear in a test in this language.',
    noVersion: 'The question has no version to show yet.',
    openPlaceholder: 'Write your answer',
    ordering: {
      correctPosition: 'Correct place: {{position}}',
      hint: 'Drag the answers or use the arrows to put them in order.',
      moveDown: 'Move down: {{answer}}',
      moveUp: 'Move up: {{answer}}',
    },
    questionMediaLabel: 'Question media',
    showCorrect: 'Show correct answers',
    solution: {
      explanation: 'Explanation',
      maxPoints: 'Points for the question',
    },
    unapprovedMarker: 'Text not approved, employees do not see it',
  },
  reasonDialog: {
    cancel: 'Cancel',
    reason: 'Reason',
    reasonPlaceholder: 'Briefly say why.',
  },
  reviewMark: {
    clear: 'Remove mark',
    dialog: {
      cancel: 'Cancel',
      confirm: 'Mark for review',
      description:
        'The question will get a mark that is visible in the list, in the question and in review mode.',
      reason: 'Reason (optional)',
      reasonPlaceholder: 'What should the reviewer look at',
      title: 'Mark for review',
    },
    mark: 'Mark for review',
    markedBy: 'Marked by {{person}}, {{date}}',
    noReason: 'No reason given.',
    notifications: {
      cleared: 'The review mark was removed.',
      clearFailed: 'The mark could not be removed. Try again.',
      marked: 'The question was marked for review.',
      markFailed: 'The question could not be marked. Try again.',
    },
    title: 'For review',
  },
  searchField: {
    clear: 'Clear search',
    minLength_one: 'Type at least {{count}} character.',
    minLength_other: 'Type at least {{count}} characters.',
    placeholder: 'Search',
  },
  statusAction: {
    actions: {
      [StatusActionEnum.ACTIVATE]: 'Activate',
      [StatusActionEnum.RESTORE]: 'Restore',
      [StatusActionEnum.RETIRE]: 'Retire',
    },
    saveFirst: 'Save your changes first.',
  },
  testPerson: {
    cannotStart: 'Cannot start the test',
    canStart: 'Can start the test',
    fields: {
      granted: 'Extra granted',
      invalidated: 'Invalidated',
      left: 'Left',
      limit: 'Limit',
      used: 'Used',
    },
    forbidden: {
      description: 'Ask the administrator for access if you need it.',
      title: "You do not have access to this person's standing",
    },
    grants: {
      empty: 'No extra attempts have been granted.',
      meta: '{{date}} · By: {{by}}',
      title: 'Extra attempts granted',
    },
    lock: {
      description: 'Locked {{date}}. By: {{by}}. Reason: {{reason}}',
      title: 'Attempts are locked',
    },
    lockedAfterPass: 'Closed after a pass',
    noLimit: 'No limit',
    passed: 'Test passed',
  },
  uploadField: {
    tooLarge: 'The file is too large. The limit is {{maxFileSize}}.',
    tooManyFiles: 'Add one file at a time.',
    wrongFormat: 'This file format is not supported.',
  },
};
